import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { CUSTOMER_TYPES, CAN_MASTER } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { fmtDate } from "@/lib/format";
import { toast } from "sonner";
import { Loader2, Plus, Search, Pencil, Building2 } from "lucide-react";

const empty = { type: CUSTOMER_TYPES[0], company_name: "", pic_name: "", phone: "", email: "", address: "", npwp: "", payment_terms: "", notes: "", active: true };

export default function Customers() {
  const { user } = useAuth();
  const canEdit = user && CAN_MASTER.includes(user.role);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/customers", { params: q ? { q } : {} }); setList(data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (c) => { setForm({ ...empty, ...c }); setEditId(c.id); setOpen(true); };

  const save = async (force = false) => {
    if (!form.company_name) { toast.error("Nama customer wajib diisi."); return; }
    setSaving(true);
    try {
      if (editId) { await api.put(`/customers/${editId}`, form); toast.success("Customer diperbarui"); }
      else { await api.post("/customers", { ...form, force }); toast.success("Customer dibuat"); }
      setOpen(false); load();
    } catch (err) {
      const detail = formatApiErrorDetail(err.response?.data?.detail);
      if (err.response?.status === 409 && !editId) {
        if (window.confirm(detail + "\n\nTetap simpan?")) { await save(true); return; }
      } else { toast.error(detail); }
    } finally { setSaving(false); }
  };

  const toggle = async (c) => {
    try { await api.patch(`/customers/${c.id}/status`); toast.success("Status diperbarui"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4" data-testid="customers-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div><h1 className="font-heading text-2xl font-bold text-navy">Master Customer</h1>
          <p className="text-sm text-muted-foreground">{list.length} customer</p></div>
        {canEdit && <Button onClick={openNew} data-testid="new-customer-btn" className="bg-primary hover:bg-red-700"><Plus size={16} className="mr-1.5" /> Tambah Customer</Button>}
      </div>

      <Card className="p-3">
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} data-testid="customer-search" placeholder="Cari nama, PIC, telepon, Customer ID..." className="pl-9" />
        </form>
      </Card>

      {loading ? <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div> : (
        list.length === 0 ? <Card className="p-10 text-center text-muted-foreground"><Building2 className="mx-auto mb-2 opacity-40" /> Belum ada customer.</Card> : (
          <>
            <Card className="hidden md:block overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50"><tr className="text-left text-xs text-muted-foreground">
                  <th className="py-2.5 px-3">Customer ID</th><th className="px-3">Nama</th><th className="px-3">Jenis</th><th className="px-3">PIC</th><th className="px-3">Telepon</th><th className="px-3">Status</th><th></th>
                </tr></thead>
                <tbody>
                  {list.map((c) => (
                    <tr key={c.id} data-testid={`customer-row-${c.customer_id}`} className="border-t hover:bg-slate-50">
                      <td className="py-2.5 px-3 font-mono-k3 text-xs">{c.customer_id}</td>
                      <td className="px-3 font-medium">{c.company_name}</td>
                      <td className="px-3 text-xs">{c.type}</td>
                      <td className="px-3 text-xs">{c.pic_name || "-"}</td>
                      <td className="px-3 text-xs">{c.phone || "-"}</td>
                      <td className="px-3"><span className={`text-xs px-2 py-0.5 rounded-full ${c.active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{c.active ? "Active" : "Inactive"}</span></td>
                      <td className="px-3">{canEdit && <div className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => openEdit(c)} data-testid={`edit-customer-${c.customer_id}`}><Pencil size={14} /></Button>
                        <Switch checked={c.active} onCheckedChange={() => toggle(c)} data-testid={`toggle-customer-${c.customer_id}`} />
                      </div>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
            <div className="md:hidden space-y-2">
              {list.map((c) => (
                <Card key={c.id} data-testid={`customer-card-${c.customer_id}`} className="p-3">
                  <div className="flex justify-between"><span className="font-mono-k3 text-xs">{c.customer_id}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${c.active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{c.active ? "Active" : "Inactive"}</span></div>
                  <div className="font-semibold mt-1">{c.company_name}</div>
                  <div className="text-xs text-muted-foreground">{c.type} · {c.pic_name || "-"} · {c.phone || "-"}</div>
                  {canEdit && <Button size="sm" variant="outline" className="mt-2" onClick={() => openEdit(c)}>Edit</Button>}
                </Card>
              ))}
            </div>
          </>
        )
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editId ? "Edit Customer" : "Tambah Customer"}</DialogTitle>
            <DialogDescription>Isi data master customer.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1"><Label>Jenis Customer</Label>
              <Select value={form.type} onValueChange={(v) => set("type", v)}>
                <SelectTrigger data-testid="customer-type"><SelectValue /></SelectTrigger>
                <SelectContent>{CUSTOMER_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1"><Label>Nama Perusahaan / Customer *</Label>
              <Input value={form.company_name} onChange={(e) => set("company_name", e.target.value)} data-testid="customer-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Nama PIC</Label><Input value={form.pic_name} onChange={(e) => set("pic_name", e.target.value)} data-testid="customer-pic" /></div>
              <div className="space-y-1"><Label>Telepon / WhatsApp</Label><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="customer-phone" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Email</Label><Input value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="customer-email" /></div>
              <div className="space-y-1"><Label>NPWP (opsional)</Label><Input value={form.npwp} onChange={(e) => set("npwp", e.target.value)} data-testid="customer-npwp" /></div>
            </div>
            <div className="space-y-1"><Label>Alamat</Label><Textarea value={form.address} onChange={(e) => set("address", e.target.value)} rows={2} data-testid="customer-address" /></div>
            <div className="space-y-1"><Label>Syarat Pembayaran</Label><Input value={form.payment_terms} onChange={(e) => set("payment_terms", e.target.value)} placeholder="cth: Net 30" data-testid="customer-terms" /></div>
            <div className="space-y-1"><Label>Catatan</Label><Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2} data-testid="customer-notes" /></div>
            <div className="flex items-center gap-2"><Switch checked={form.active} onCheckedChange={(v) => set("active", v)} data-testid="customer-active" /><Label>Active</Label></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Batal</Button>
            <Button onClick={() => save(false)} disabled={saving} data-testid="save-customer-btn" className="bg-primary hover:bg-red-700">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Simpan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
